import React, { useEffect, useRef, useState, useSyncExternalStore } from "react"
import { createRoot } from "react-dom/client"
import { useNavigate } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArrowRight,
  faGripVertical,
  faXmark,
} from "@fortawesome/free-solid-svg-icons"

import { useNana } from "../../../hooks/useNana"
import { ID, Idea, Project, uuid } from "../../../../model/types"
import IdeaBox from "../../../components/idea_box"
import ImageStore from "../mosaic_page/idea_pane/editor/image/store"
import { ImageView, store } from "../mosaic_page/idea_pane/editor/image"
import { store as imageStore } from "../mosaic_page/idea_pane/editor/image"
import * as Commands from "../../../../model/commands/index"
import Toggle from "./toggle"
import { MosaicPageState } from "../mosaic"

export default function ProjectContentExpanded({
  project,
  setMode,
}: {
  project: Project
  setMode: (mode: "organize" | "draft") => void
}) {
  const { model } = useNana()
  const [ideaIds, setIdeaIds] = useState<ID[]>([])

  useEffect(() => {
    setIdeaIds(project.ideaIds)
  }, [project.id, model])

  useEffect(() => {
    const ideas = project.ideaIds.map((id) => model.fetchIdea(id))
    const uuids: uuid[] = ideas
      .map((idea) => idea.content)
      .flatMap((content) => {
        if (!content) return []
        return content.match(/<img uuid="([^"]+)"/g)?.map((match) => {
          return match.match(/<img uuid="([^"]+)"/)?.[1]
        })
      })
      .filter((uuid) => uuid !== undefined) as uuid[]

    store.fetchImageSignedUrlsFromUuids(uuids)
  }, [])

  const ideas = ideaIds.map((id) => model.fetchIdea(id))

  return (
    <div className="mt-2  flex flex-col overflow-y-scroll min-h-0 select-none">
      <div className="flex gap-2 justify-between items-center bg-gray-50 dark:bg-gray-800 p-4 border-y border-gray-200 dark:border-gray-600">
        <Toggle mode={"draft"} setMode={setMode} />

        <div
          onClick={() => copyContent(ideas)}
          className="cursor-pointer text-xs md:text-sm px-2 py-1 bg-sky-100 hover:bg-sky-200 dark:bg-sky-900 flex items-center gap-2 rounded hover:shadow dark:hover:bg-sky-800"
        >
          Copy content
        </div>
      </div>
      <div className="p-4 grow flex flex-col overflow-y-scroll min-h-0">
        {ideas.map((idea) => (
          <IdeaItem key={idea.id} idea={idea} project={project} />
        ))}
        {ideas.length === 0 && <EmptyState />}
      </div>
    </div>
  )
}

const EmptyState = () => {
  return (
    <div className="grow flex flex-col gap-4 items-center justify-start pt-4 md:pt-24 text-sm md:text-base text-gray-700">
      No ideas added yet.
    </div>
  )
}
const IdeaItem = ({ idea, project }: { idea: Idea; project: Project }) => {
  const { model, commander } = useNana()
  const navigate = useNavigate()

  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!contentRef.current) return

    const imageTags = Array.from(contentRef.current.getElementsByTagName("img"))
    imageTags.forEach((imageTag) => {
      const uuid = imageTag.attributes["uuid"]?.value
      if (uuid) {
        const div = document.createElement("div")
        imageTag.replaceWith(div)
        createRoot(div).render(
          <ImageView uuid={uuid} selected={false} editable={false} />
        )
      }
    })

    const onClick = (e: MouseEvent) => {
      const dataset = (e.target as any)?.dataset
      if (dataset?.type === "page-link") {
        const id = dataset.id
        const idea = model.fetchIdea(id)
        if (idea) clickedOnIdea(idea)
      }
    }
    contentRef.current.addEventListener("click", onClick)

    return () => {
      contentRef.current?.removeEventListener("click", onClick)
    }
  }, [idea.id])

  const removeIdeaFromProject = (idea) => {
    commander.exec(
      new Commands.SetProjectFieldsCommand({
        id: project.id,
        fields: {
          ideaIds: project.ideaIds.filter((id) => id != idea.id),
        },
      })
    )
  }

  const clickedOnIdea = (idea: Idea) => {
    MosaicPageState.forceOpenLocation = idea.location
    navigate("/app")
  }

  return (
    <div className="group mt-6 px-2 flex justify-start items-start">
      <div className="flex flex-col h-full justify-stretch items-center select-none">
        <div className="grow-0">
          <IdeaBox idea={idea} />
        </div>
        <div
          className="h-full flex-grow border-l-2 border-opacity-40 -mb-10"
          style={{ borderColor: idea.colour }}
        ></div>
      </div>
      <div className="grow pl-2 md:pl-8 select-text flex flex-col">
        <div className="grow flex justify-between items-center">
          <div className="grow text-base md:text-lg font-medium">
            {idea.title}
          </div>
          <div className="hidden group-hover:flex gap-2">
            <div
              className=" text-gray-300 hover:text-gray-500 cursor-pointer"
              onClick={() => clickedOnIdea(idea)}
            >
              <FontAwesomeIcon icon={faArrowRight} className="text-lg" />
            </div>
            <div
              className=" text-gray-300 hover:text-gray-500 cursor-pointer"
              onClick={() => removeIdeaFromProject(idea)}
            >
              <FontAwesomeIcon icon={faXmark} className="text-lg" />
            </div>
          </div>
        </div>
        <div
          className="mt-4 prose dark:prose-invert prose-sm md:prose-base"
          ref={contentRef}
          dangerouslySetInnerHTML={{ __html: idea.content }}
        />
      </div>
    </div>
  )
}

const copyContent = (ideas) => {
  const htmls: string[] = []
  ideas.forEach((idea) => {
    if (idea.title) htmls.push("<h1>" + idea.title + "</h1>")
    if (idea.content) htmls.push(transformDomForCopy(idea.content))
  })
  const content = htmls.join("\n\n")
  try {
    const blobInput = new Blob([content], { type: "text/html" })
    const clipboardItemInput = new ClipboardItem({ "text/html": blobInput })
    navigator.clipboard.write([clipboardItemInput])
  } catch (e) {
    // Handle error with user feedback - "Copy failed!" kind of thing
    console.error(e)
  }
}

const transformDomForCopy = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, "text/html")
  doc.querySelectorAll("a[href]").forEach((linkTag) => {
    linkTag.removeAttribute("class")
    linkTag.removeAttribute("target")
  })
  doc.querySelectorAll("a[data-type='page-link']").forEach((tag: Element) => {
    // @ts-expect-error
    delete tag.dataset.type
    // @ts-expect-error
    delete tag.dataset.label
  })
  doc.querySelectorAll("math-inline").forEach((tag: Element) => {
    const code = document.createElement("code")
    code.setAttribute("type", "latex")
    code.innerHTML = tag.getAttribute("latex")!
    tag.replaceWith(code)
  })
  doc.querySelectorAll("math-block").forEach((tag: Element) => {
    const pre = document.createElement("pre")
    const code = document.createElement("code")
    pre.appendChild(code)
    code.innerHTML = tag.getAttribute("latex")!
    code.setAttribute("type", "latex")
    tag.replaceWith(pre)
  })
  doc.querySelectorAll("img[uuid]").forEach((imageTag: Element) => {
    const uuid = imageTag.getAttribute("uuid")
    if (!uuid) {
      imageTag.remove()
      return
    }
    const imageState = store.getImageState(uuid)
    if (!imageState) {
      imageTag.remove()
      return
    }
    if (imageState.type !== "with-url") {
      imageTag.remove()
      return
    }
    imageTag.setAttribute("src", imageState.signedUrl.toString())
    imageTag.removeAttribute("uuid")
  })
  return doc.body.innerHTML
}
